// App Configuration
export const APP_NAME = 'GH Pharmacy Dashboard'
export const APP_VERSION = '1.0.0'

// Timezone
export const TIMEZONE = 'Asia/Baghdad'
export const TIMEZONE_OFFSET = 3 // UTC+3

// Currency
export const CURRENCY_CODE = 'IQD'
export const CURRENCY_SYMBOL = 'د.ع'
export const CURRENCY_NAME = 'Iraqi Dinar'

// Pharmacy Location
export const PHARMACY_LOCATION = {
  latitude: 33.3568206,
  longitude: 44.4044026,
  radius: 100, // meters
}

// Shifts
export const SHIFTS = {
  MORNING: {
    id: 'morning',
    name: 'Morning Shift',
    nameAr: 'الوردية الصباحية',
    start: '09:00',
    end: '18:00',
    days: [0, 1, 2, 3, 4, 6], // Saturday-Thursday (0=Saturday in Iraq)
  },
  EVENING: {
    id: 'evening',
    name: 'Evening Shift',
    nameAr: 'الوردية المسائية',
    start: '18:00',
    end: '00:00',
    days: [0, 1, 2, 3, 4, 6],
  },
  FRIDAY: {
    id: 'friday',
    name: 'Friday Shift',
    nameAr: 'وردية الجمعة',
    start: '15:00',
    end: '00:00',
    days: [5], // Friday only
  },
}

// Staff Roles
export const STAFF_ROLES = {
  OWNER: 'owner',
  MANAGER: 'manager',
  PURCHASE_MANAGER: 'purchase_manager',
  PHARMACIST: 'pharmacist',
  ASSISTANT: 'assistant',
} as const

export const STAFF_ROLE_LABELS = {
  owner: { en: 'Owner', ar: 'المالك' },
  manager: { en: 'Manager', ar: 'مدير' },
  finance: { en: 'Finance', ar: 'المحاسبة' },
  purchase_manager: { en: 'Purchase Manager', ar: 'مدير المشتريات' },
  pharmacist: { en: 'Pharmacist', ar: 'صيدلي' },
  assistant: { en: 'Assistant', ar: 'مساعد' },
  driver: { en: 'Driver', ar: 'سائق' },
}

/**
 * Roles offered when CREATING a staff member.
 *
 * owner is deliberately absent: it bypasses every permission check, and
 * staff_admin_create refuses it server-side. Offering it in the form would only
 * produce an error the user cannot act on.
 */
export const CREATABLE_STAFF_ROLES = Object.keys(STAFF_ROLE_LABELS)
  .filter((r) => r !== 'owner') as Array<keyof typeof STAFF_ROLE_LABELS>

// Manager roles that can access the dashboard.
//
// Compared CASE-INSENSITIVELY. Production stores 'Finance' with a capital F
// while every other role is lowercase, so an exact-match list silently denied
// manager access to anyone whose row spelled it differently.
export const MANAGER_ROLES = ['owner', 'manager', 'finance'] as const

export function isManagerRole(role: string | null | undefined): boolean {
  if (!role) return false
  return (MANAGER_ROLES as readonly string[]).includes(role.toLowerCase())
}

// Note priorities
export const NOTE_PRIORITIES = {
  NORMAL: 'normal',
  URGENT: 'urgent',
} as const

export const NOTE_PRIORITY_LABELS = {
  normal: { en: 'Normal', ar: 'عادي' },
  urgent: { en: 'Urgent', ar: 'عاجل' },
}

// Note categories
export const NOTE_CATEGORIES = {
  GENERAL: 'general',
  ORDER: 'order',
  REMINDER: 'reminder',
  ISSUE: 'issue',
} as const

export const NOTE_CATEGORY_LABELS = {
  general: { en: 'General', ar: 'عام' },
  order: { en: 'Customer Order', ar: 'طلب زبون' },
  reminder: { en: 'Reminder', ar: 'تذكير' },
  issue: { en: 'Issue', ar: 'مشكلة' },
}

// Salary types
export const SALARY_TYPES = {
  FIXED: 'fixed',
  HOURLY: 'hourly',
} as const

export const SALARY_TYPE_LABELS = {
  fixed: { en: 'Fixed Monthly', ar: 'راتب شهري ثابت' },
  hourly: { en: 'Hourly Rate', ar: 'أجر بالساعة' },
}

// Payment statuses
export const PAYMENT_STATUSES = {
  PENDING: 'pending',
  PAID: 'paid',
  PARTIAL: 'partial',
} as const

export const PAYMENT_STATUS_LABELS = {
  pending: { en: 'Pending', ar: 'قيد الانتظار' },
  paid: { en: 'Paid', ar: 'مدفوع' },
  partial: { en: 'Partial', ar: 'جزئي' },
}

// Goal categories
export const GOAL_CATEGORIES = {
  SALES: 'sales',
  INVENTORY: 'inventory',
  STAFF: 'staff',
} as const

// Announcement priorities
export const ANNOUNCEMENT_PRIORITIES = {
  NORMAL: 'normal',
  IMPORTANT: 'important',
  URGENT: 'urgent',
} as const

export const ANNOUNCEMENT_PRIORITY_LABELS = {
  normal: { en: 'Normal', ar: 'عادي' },
  important: { en: 'Important', ar: 'مهم' },
  urgent: { en: 'Urgent', ar: 'عاجل' },
}

// Activity action types
export const ACTIVITY_ACTIONS = {
  LOGIN: 'login',
  LOGOUT: 'logout',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  VIEW: 'view',
  EXPORT: 'export',
  IMPORT: 'import',
} as const

// Entity types for activity logs
export const ENTITY_TYPES = {
  STAFF: 'staff',
  PRODUCT: 'product',
  ATTENDANCE: 'attendance',
  SALARY: 'salary',
  INCOME: 'income',
  NOTE: 'note',
  PATIENT: 'patient',
  REPORT: 'report',
  SETTING: 'setting',
  ANNOUNCEMENT: 'announcement',
  GOAL: 'goal',
} as const

// Default pagination
export const DEFAULT_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

// Date range presets
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  THIS_WEEK: 'this_week',
  LAST_WEEK: 'last_week',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  THIS_YEAR: 'this_year',
  CUSTOM: 'custom',
} as const

export const DATE_RANGE_PRESET_LABELS = {
  today: { en: 'Today', ar: 'اليوم' },
  yesterday: { en: 'Yesterday', ar: 'أمس' },
  this_week: { en: 'This Week', ar: 'هذا الأسبوع' },
  last_week: { en: 'Last Week', ar: 'الأسبوع الماضي' },
  this_month: { en: 'This Month', ar: 'هذا الشهر' },
  last_month: { en: 'Last Month', ar: 'الشهر الماضي' },
  this_year: { en: 'This Year', ar: 'هذا العام' },
  custom: { en: 'Custom Range', ar: 'نطاق مخصص' },
}

// Chart colors
export const CHART_COLORS = {
  primary: '#00cd4f',
  secondary: '#0d9488',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#22c55e',
  info: '#3b82f6',
  muted: '#64748b',
}

// Chart color palette for multiple series
export const CHART_COLOR_PALETTE = [
  '#00cd4f', // Primary green
  '#0d9488', // Teal
  '#3b82f6', // Blue
  '#f59e0b', // Amber
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
]

// Notification types
export const NOTIFICATION_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
} as const

// Local storage keys
export const STORAGE_KEYS = {
  THEME: 'ghp-dashboard-theme',
  LANGUAGE: 'ghp-dashboard-language',
  SIDEBAR_COLLAPSED: 'ghp-dashboard-sidebar-collapsed',
  DASHBOARD_LAYOUT: 'ghp-dashboard-layout',
}

// Animation durations (ms)
export const ANIMATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
}

// Breakpoints (match Tailwind)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}
